/** The calls the page makes before the SDK script arrives. */
const QUEUED_METHODS = ['init', 'add', 'track', 'logEvent', 'flush'] as const

/** One deferred call, in the shape Browser SDK 2.x replays: `{ name, args }`. */
interface AmplitudeQueuedCall {
  name: (typeof QUEUED_METHODS)[number]
  args: unknown[]
}

/**
 * Install `window.amplitude` as a command queue the Amplitude Browser SDK
 * 2.x drains when its script loads: the SDK merges its API into this object
 * and, because `invoked` is set, replays every `_q` entry through it — the
 * `init` first, then the `add` of our plugins (`consentModePlugin`), then
 * each `track` emitted while the script was in flight.
 *
 * Both details are load-bearing. Until 2026-10-22 the page used the
 * amplitude-js v8 snippet: no `invoked` flag (the SDK skipped the replay)
 * and `[name, ...args]` entries (the SDK reads `entry.name` / `entry.args`),
 * so the `init` never ran and the web sent Amplitude nothing — while the
 * SDK itself loaded fine. Returns without touching an existing
 * `window.amplitude`.
 */
export function installAmplitudeCommandQueue(): void {
  if (window.amplitude) return
  const queue: AmplitudeQueuedCall[] = []
  const stub: Record<string, unknown> = { _q: queue, _iq: {}, invoked: true }
  for (const name of QUEUED_METHODS) {
    stub[name] = (...args: unknown[]) => {
      queue.push({ name, args })
    }
  }
  window.amplitude = stub
}
